// src/context/DataContext.js - Enhance your existing context

import React, { createContext, useContext, useState, useEffect } from 'react';
import { supabase } from '../utils/supabaseIntegration';

const DataContext = createContext();

export const useData = () => {
  const context = useContext(DataContext);
  if (!context) {
    throw new Error('useData must be used within a DataProvider');
  }
  return context;
};

export const DataProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [clientData, setClientData] = useState([]);
  const [sheetConfig, setSheetConfig] = useState(null);
  const [portalConfig, setPortalConfig] = useState(null);
  const [portals, setPortals] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [lastFetched, setLastFetched] = useState(null);
  const [error, setError] = useState(null);

  // Cache duration in milliseconds (e.g., 5 minutes)
  const CACHE_DURATION = 5 * 60 * 1000;

  // Load user and initialize data
  useEffect(() => {
    const loadUserAndData = async () => {
      try {
        // Get current session
        const {
          data: { session },
        } = await supabase.auth.getSession();

        if (session?.user) {
          setUser(session.user);

          // Load sheet configuration and portal configs for this user
          await loadSheetConfig(session.user.id);
          await loadPortalConfigs(session.user.id);
        } else {
          setIsLoading(false);
        }
      } catch (error) {
        console.error('Error loading user data:', error);
        setError('Failed to load user data');
        setIsLoading(false);
      }
    };

    loadUserAndData();

    // Set up auth state listener
    const { data: authListener } = supabase.auth.onAuthStateChange(
      async (event, session) => {
        console.log('Auth state changed:', event, {
          userId: session?.user?.id,
          hasSession: !!session,
        });

        if (event === 'SIGNED_IN' && session?.user) {
          console.log('🔐 User signed in, loading configurations');
          setUser(session.user);
          await loadSheetConfig(session.user.id);
          await loadPortalConfigs(session.user.id);
        } else if (event === 'SIGNED_OUT') {
          setUser(null);
          setSheetConfig(null);
          setPortalConfig(null);
          setPortals([]);
          setClientData([]);
          setLastFetched(null);
        }
      }
    );

    return () => {
      authListener?.subscription?.unsubscribe();
    };
    // eslint-disable-next-line
  }, []);

  // Load sheet configuration from Supabase
  const loadSheetConfig = async (userId) => {
    try {
      console.log('Loading configurations for user:', userId);
      setIsLoading(true);

      // Fetch the sheet configuration from database
      const { data, error } = await supabase
        .from('sheet_configs')
        .select('*')
        .eq('user_id', userId)
        .order('created_at', { ascending: false })
        .limit(1)
        .single();

      if (error && error.code !== 'PGRST116') {
        // PGRST116 means no rows returned
        console.error('Error loading sheet config:', error);
        setError('Failed to load configuration');
      } else if (data) {
        console.log('✅ Loaded sheet configuration:', data);
        setSheetConfig(data);

        // After loading config, fetch the client data
        await fetchClientData(userId, data);
      } else {
        console.log('No sheet configuration found');
      }

      setIsLoading(false);
    } catch (err) {
      console.error('Error in loadSheetConfig:', err);
      setError('Failed to load configuration');
      setIsLoading(false);
    }
  };

  // Load portal configurations for this user
  const loadPortalConfigs = async (userId) => {
    try {
      // Load portals with their configs
      const { data: portalData, error: portalError } = await supabase
        .from('portals')
        .select(`
          *,
          portal_configs (*)
        `)
        .eq('business_id', userId)
        .order('created_at', { ascending: false });

      if (portalError) {
        console.error('Error loading portals:', portalError);
      } else if (portalData) {
        setPortals(portalData);
        // Set the first portal's config as the active one
        if (portalData.length > 0 && portalData[0].portal_configs?.length > 0) {
          setPortalConfig(portalData[0].portal_configs[0]);
        }
      }
    } catch (err) {
      console.error('Error in loadPortalConfigs:', err);
    }
  };

  // Save portal configuration to Supabase
  const savePortalConfig = async (portalId, configData) => {
    try {
      if (!user) throw new Error('User not authenticated');

      const { branding, display_config, status_config, search_config, workload_config } = configData;

      // Upsert portal_configs row
      const { data: existingConfig } = await supabase
        .from('portal_configs')
        .select('id')
        .eq('portal_id', portalId)
        .limit(1)
        .single();

      let result;
      if (existingConfig) {
        // Update existing
        const { data, error } = await supabase
          .from('portal_configs')
          .update({
            branding,
            display_config,
            status_config,
            search_config,
            workload_config,
            user_id: user.id,
            company_name: branding?.company_name || '',
            primary_color: branding?.primary_color || '#3B82F6',
            background_color: branding?.background_color || '#F9FAFB',
            logo_url: branding?.logo_url || '',
            custom_message: display_config?.welcome_message || '',
            show_estimated_completion: display_config?.show_estimated_completion ?? true,
            show_priority: display_config?.show_priority ?? true,
            show_notes: display_config?.show_notes ?? true,
            allow_client_updates: display_config?.allow_client_updates ?? false,
            updated_at: new Date().toISOString(),
          })
          .eq('portal_id', portalId)
          .select()
          .single();

        if (error) throw error;
        result = data;
      } else {
        // Insert new
        const { data, error } = await supabase
          .from('portal_configs')
          .insert({
            portal_id: portalId,
            branding,
            display_config,
            status_config,
            search_config,
            workload_config,
            user_id: user.id,
            company_name: branding?.company_name || '',
            primary_color: branding?.primary_color || '#3B82F6',
            background_color: branding?.background_color || '#F9FAFB',
            logo_url: branding?.logo_url || '',
            custom_message: display_config?.welcome_message || '',
            show_estimated_completion: display_config?.show_estimated_completion ?? true,
            show_priority: display_config?.show_priority ?? true,
            show_notes: display_config?.show_notes ?? true,
            allow_client_updates: display_config?.allow_client_updates ?? false,
          })
          .select()
          .single();

        if (error) throw error;
        result = data;
      }

      setPortalConfig(result);
      // Refresh portals list
      await loadPortalConfigs(user.id);
      return result;
    } catch (err) {
      console.error('Error saving portal config:', err);
      throw err;
    }
  };

  // Create a new portal
  const createPortal = async (portalData) => {
    try {
      if (!user) throw new Error('User not authenticated');

      const { data: portal, error: portalError } = await supabase
        .from('portals')
        .insert({
          business_id: user.id,
          name: portalData.name,
          slug: portalData.slug,
          google_sheet_id: portalData.googleSheetId || '',
          sheet_name: portalData.sheetName || 'Sheet1',
          status: 'active',
        })
        .select()
        .single();

      if (portalError) throw portalError;

      // Create default portal config
      await savePortalConfig(portal.id, {
        branding: {
          company_name: portalData.name,
          primary_color: '#3B82F6',
          secondary_color: '#1F2937',
          background_color: '#F9FAFB',
          font_family: 'Inter',
          logo_url: '',
          contact_email: '',
          contact_phone: '',
          address: '',
        },
        display_config: {
          title: 'Client Status Tracker',
          welcome_message: 'Track the status of your project with us',
          show_progress_bar: true,
          show_estimated_completion: true,
          show_contact_info: true,
          show_priority: true,
          show_notes: true,
          allow_client_updates: false,
          enable_notifications: false,
        },
        status_config: [
          { id: 'completed', name: 'Completed', icon: '✅', color: '#22C55E', position: 1 },
          { id: 'in-progress', name: 'In Progress', icon: '🔨', color: '#3B82F6', position: 2 },
          { id: 'review', name: 'Review', icon: '🔍', color: '#EAB308', position: 3 },
          { id: 'awaiting-documents', name: 'Awaiting Documents', icon: '📝', color: '#EF4444', position: 4 },
          { id: 'in-queue', name: 'In Queue', icon: '⏳', color: '#A1A1AA', position: 5 },
        ],
        search_config: {
          search_type: 'fuzzy',
          case_sensitive: false,
          enable_filters: true,
          search_columns: ['name', 'email'],
        },
        workload_config: {
          team_size: 1,
          daily_completion_rate: 3,
          working_days: [1, 2, 3, 4, 5],
          business_hours: { start: '09:00', end: '17:00' },
          overtime_enabled: false,
        },
      });

      await loadPortalConfigs(user.id);
      return portal;
    } catch (err) {
      console.error('Error creating portal:', err);
      throw err;
    }
  };

  // Fetch client data from Supabase cache or Google Sheets
  const fetchClientData = async (userId, config) => {
    try {
      setIsLoading(true);
      setError(null);

      if (!config || !config.sheet_id) {
        console.log('No sheet configuration available');
        setIsLoading(false);
        return;
      }

      // Check for cached data in Supabase
      const { data: cachedData, error: cacheError } = await supabase
        .from('client_data_cache')
        .select('*')
        .eq('user_id', userId)
        .eq('sheet_id', config.sheet_id)
        .order('created_at', { ascending: false })
        .limit(1)
        .single();

      // If we have recent cached data, use it
      if (
        !cacheError &&
        cachedData &&
        cachedData.data &&
        cachedData.created_at
      ) {
        const cacheTime = new Date(cachedData.created_at).getTime();
        const now = new Date().getTime();

        if (now - cacheTime < CACHE_DURATION) {
          console.log(
            '✅ Using cached data from',
            new Date(cachedData.created_at).toLocaleString()
          );
          setClientData(cachedData.data);
          setLastFetched(new Date(cachedData.created_at));
          setIsLoading(false);
          return;
        } else {
          console.log('⚠️ Cache expired, fetching fresh data');
        }
      } else {
        console.log('⚠️ No cached data found or cache error');
      }

      // Get fresh data from Google Sheets via our proxy
      const freshData = await fetchGoogleSheetsData(config.sheet_id);

      // Cache the fresh data in Supabase
      await cacheClientData(userId, config.sheet_id, freshData);

      setClientData(freshData);
      setLastFetched(new Date());
      setIsLoading(false);
    } catch (err) {
      console.error('Error fetching client data:', err);
      setError('Failed to fetch client data: ' + err.message);
      setIsLoading(false);
    }
  };

  // Fetch data from Google Sheets through our proxy
  const fetchGoogleSheetsData = async (sheetId) => {
    try {
      console.log('🔄 Fetching fresh data from Google Sheets');

      // Get auth token from session
      const {
        data: { session },
      } = await supabase.auth.getSession();
      if (!session?.access_token) {
        throw new Error('No authentication token available');
      }

      const headers = {
        Authorization: `Bearer ${session.access_token}`,
        'Content-Type': 'application/json',
      };

      const response = await fetch(
        `/.netlify/functions/google-sheets-proxy?sheetId=${sheetId}`,
        {
          method: 'GET',
          headers,
        }
      );

      if (!response.ok) {
        let errorMessage = `HTTP error ${response.status}`;
        try {
          const errorData = await response.json();
          errorMessage = errorData.message || errorData.error || errorMessage;
        } catch (e) {
          // If we can't parse JSON, use the status text
          errorMessage = `HTTP error ${response.status}: ${response.statusText}`;
        }
        throw new Error(errorMessage);
      }

      const result = await response.json();

      if (!result.success || !result.data) {
        throw new Error(result.message || 'Failed to fetch sheet data');
      }

      // Process the raw data into client records
      return processSheetData(result.data);
    } catch (error) {
      console.error('Error in fetchGoogleSheetsData:', error);
      throw error;
    }
  };

  // Process raw sheet data into client records
  const processSheetData = (data) => {
    const { headers, rows, columnSuggestions } = data;

    if (!headers || !rows || !columnSuggestions) {
      console.error('Invalid data format:', data);
      throw new Error('Invalid data format received from server');
    }

    // Map rows to client objects using column suggestions
    return rows.map((row, index) => {
      const client = {
        id: row[columnSuggestions.id] || `client-${index}`,
        name: row[columnSuggestions.name] || 'Unknown',
        email: row[columnSuggestions.email] || '',
        status: row[columnSuggestions.status] || 'Unknown',
        priority:
          columnSuggestions.priority !== undefined
            ? row[columnSuggestions.priority]
            : null,
        date:
          columnSuggestions.date !== undefined
            ? row[columnSuggestions.date]
            : '',
        notes:
          columnSuggestions.notes !== undefined
            ? row[columnSuggestions.notes]
            : '',
        rawData: row, // Keep the original row for reference
      };

      return client;
    });
  };

  // Cache client data in Supabase
  const cacheClientData = async (userId, sheetId, data) => {
    try {
      console.log('📦 Caching client data in Supabase');

      // First, check if there's an existing cache record
      const { data: existingCache } = await supabase
        .from('client_data_cache')
        .select('id')
        .eq('user_id', userId)
        .eq('sheet_id', sheetId)
        .limit(1);

      const currentTime = new Date().toISOString();

      // If there's an existing cache, update it
      if (existingCache && existingCache.length > 0) {
        const { error } = await supabase
          .from('client_data_cache')
          .update({
            data: data,
            created_at: currentTime,
            updated_at: currentTime,
          })
          .eq('id', existingCache[0].id);

        if (error) {
          console.error('Error updating cache:', error);
        } else {
          console.log('✅ Cache updated successfully');
        }
      }
      // Otherwise, create a new cache record
      else {
        const { error } = await supabase.from('client_data_cache').insert({
          user_id: userId,
          sheet_id: sheetId,
          data: data,
          created_at: currentTime,
          updated_at: currentTime,
        });

        if (error) {
          console.error('Error creating cache:', error);
        } else {
          console.log('✅ New cache created successfully');
        }
      }
    } catch (err) {
      console.error('Error caching client data:', err);
      // Continue even if caching fails
    }
  };

  // Save sheet configuration
  const saveSheetConfig = async (config) => {
    try {
      if (!user) {
        throw new Error('User not authenticated');
      }

      console.log('💾 Saving sheet configuration:', config);

      const newConfig = {
        user_id: user.id,
        sheet_id: config.sheetId,
        sheet_url: config.sheetUrl,
        column_mapping: config.columnMapping,
        sheet_headers: config.detectedHeaders,
        setup_method: config.setupMethod,
        created_at: new Date().toISOString(),
      };

      const { data, error } = await supabase
        .from('sheet_configs')
        .insert(newConfig)
        .select()
        .single();

      if (error) {
        console.error('Error saving sheet config:', error);
        throw new Error('Failed to save configuration');
      }

      console.log('✅ Configuration saved successfully:', data);
      setSheetConfig(data);

      // After saving config, fetch client data
      await fetchClientData(user.id, data);

      return data;
    } catch (err) {
      console.error('Error in saveSheetConfig:', err);
      throw err;
    }
  };

  // Force refresh data
  const refreshData = async () => {
    try {
      if (!user || !sheetConfig) {
        throw new Error('User not authenticated or no configuration available');
      }

      console.log('🔄 Forcing data refresh');
      await fetchClientData(user.id, sheetConfig);
      return true;
    } catch (err) {
      console.error('Error refreshing data:', err);
      setError('Failed to refresh data: ' + err.message);
      return false;
    }
  };

  // Get filtered clients based on criteria
  const getFilteredClients = (filters = {}) => {
    if (!clientData || clientData.length === 0) {
      return [];
    }

    return clientData.filter((client) => {
      // Filter by status if specified
      if (filters.status && client.status !== filters.status) {
        return false;
      }

      // Filter by search term if provided
      if (filters.search && filters.search.trim() !== '') {
        const searchTerm = filters.search.toLowerCase();
        const nameMatch =
          client.name && client.name.toLowerCase().includes(searchTerm);
        const emailMatch =
          client.email && client.email.toLowerCase().includes(searchTerm);
        const idMatch =
          client.id && client.id.toString().toLowerCase().includes(searchTerm);

        if (!nameMatch && !emailMatch && !idMatch) {
          return false;
        }
      }

      return true;
    });
  };

  // Calculate status statistics
  const getStatusStats = () => {
    if (!clientData || clientData.length === 0) {
      return {};
    }

    const stats = {
      total: clientData.length,
      statusCounts: {},
    };

    // Count clients by status
    clientData.forEach((client) => {
      const status = client.status || 'Unknown';
      stats.statusCounts[status] = (stats.statusCounts[status] || 0) + 1;
    });

    return stats;
  };

  return (
    <DataContext.Provider
      value={{
        user,
        clientData,
        sheetConfig,
        portalConfig,
        portals,
        isLoading,
        lastFetched,
        error,
        saveSheetConfig,
        savePortalConfig,
        createPortal,
        loadPortalConfigs,
        refreshData,
        getFilteredClients,
        getStatusStats,
      }}
    >
      {children}
    </DataContext.Provider>
  );
};

export default DataContext;
